import { useEffect, useState } from "react";
import axios from "axios";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { CalendarDays, Hammer, Clock, Mail, CalendarPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (d) => {
  if (!d) return "Datum folgt";
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : d;
};

const downloadIcs = (event) => {
  const pad = (n) => String(n).padStart(2, "0");
  const [y, m, dd] = event.date.split("-").map(Number);
  const [hh, mm] = (event.time || "09:00").split(":").map(Number);
  const fmt = (d) =>
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
  const start = new Date(y, m - 1, dd, hh, mm);
  const end = new Date(y, m - 1, dd, hh + 2, mm);
  const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//KGA Schönwald e.V.//Termine//DE",
    "BEGIN:VEVENT",
    `UID:${event.id}@kga-schoenwald.de`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:${event.title}`,
    event.location ? `LOCATION:${event.location}` : null,
    event.description ? `DESCRIPTION:${event.description.replace(/\n/g, "\\n")}` : null,
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean).join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${event.title.replace(/[^\wäöüÄÖÜß-]+/g, "_")}.ics`;
  a.click();
  URL.revokeObjectURL(url);
};

const EventItem = ({ event, testid }) => (
  <div className="rounded-xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
    <p className="font-jbm text-xs tracking-wider uppercase text-[#D4A359]">
      {formatDate(event.date)}
      {event.time ? ` · ${event.time} Uhr` : ""}
      {event.location ? ` · ${event.location}` : ""}
    </p>
    <div className="mt-1.5 flex items-start justify-between gap-3">
      <p className="font-semibold text-[#F8F7F2]">{event.title}</p>
      {event.date && (
        <button
          onClick={() => downloadIcs(event)}
          data-testid={`${testid}-ics`}
          aria-label="Zum Kalender hinzufügen"
          title="Zum Kalender hinzufügen"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#D4A359]/20 text-[#D4A359] transition-transform duration-300 hover:scale-110 hover:bg-[#D4A359] hover:text-[#1A382B]"
        >
          <CalendarPlus size={15} />
        </button>
      )}
    </div>
    {event.description && (
      <p className="mt-1 text-sm leading-relaxed text-[#C4D2C8]">{event.description}</p>
    )}
  </div>
);

export default function Termine() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    axios.get(`${API}/events`).then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const termine = events.filter((e) => e.category === "termin");
  const einsaetze = events.filter((e) => e.category === "arbeitseinsatz");

  return (
    <section id="termine" className="py-16 md:py-28 bg-[#1A382B] grain relative overflow-hidden" data-testid="termine-section">
      <div className="pointer-events-none absolute -bottom-40 -left-40 h-[30rem] w-[30rem] rounded-full bg-[#2D5A45] blur-3xl opacity-60" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-[2]">
        <SectionHeading
          number="01"
          eyebrow="Termine & Arbeitseinsätze"
          title="Was in der Anlage ansteht"
          light
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Reveal>
            <div className="h-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-7" data-testid="termine-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#D4A359]/20 text-[#D4A359]">
                <CalendarDays size={21} strokeWidth={1.8} />
              </span>
              <h3 className="mt-5 font-display text-xl sm:text-2xl font-semibold text-[#F8F7F2]">Termine</h3>
              {termine.length === 0 ? (
                <>
                  <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#C4D2C8]">
                    Zur Zeit stehen keine Termine an. Sobald neue Termine des Vereins oder der Anlage
                    feststehen, werden sie hier bekannt gegeben.
                  </p>
                  <p className="mt-5 inline-block rounded-full border border-[#D4A359]/40 px-4 py-1.5 font-jbm text-xs tracking-wider uppercase text-[#D4A359]">
                    Aktuell keine Termine
                  </p>
                </>
              ) : (
                <div className="mt-4 space-y-3" data-testid="termine-list">
                  {termine.map((e, i) => (
                    <EventItem key={e.id} event={e} testid={`termin-item-${i}`} />
                  ))}
                </div>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-7" data-testid="arbeitseinsaetze-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#D4A359]/20 text-[#D4A359]">
                <Hammer size={21} strokeWidth={1.8} />
              </span>
              <h3 className="mt-5 font-display text-xl sm:text-2xl font-semibold text-[#F8F7F2]">Arbeitseinsätze</h3>
              {einsaetze.length === 0 ? (
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#C4D2C8]">
                  Aktuell sind keine Arbeitseinsätze geplant. Neue Einsätze werden hier bekannt gegeben.
                </p>
              ) : (
                <div className="mt-4 space-y-3" data-testid="arbeitseinsaetze-list">
                  {einsaetze.map((e, i) => (
                    <EventItem key={e.id} event={e} testid={`arbeitseinsatz-item-${i}`} />
                  ))}
                </div>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-7" data-testid="sprechstunde-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#D4A359]/20 text-[#D4A359]">
                <Clock size={21} strokeWidth={1.8} />
              </span>
              <h3 className="mt-5 font-display text-xl sm:text-2xl font-semibold text-[#F8F7F2]">Sprechstunden</h3>
              <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#C4D2C8]">
                Sprechstunden des Vereins können auch <strong className="text-[#F8F7F2]">Nichtmitglieder</strong> in
                Anspruch nehmen – nach vorheriger Anmeldung per Mail. Dort können allgemeine Fragen zur
                Anlage, zu Rechnungen und vielem mehr gestellt werden.
              </p>
              <a
                href="mailto:Info@kga-schoenwald.de"
                data-testid="sprechstunde-mail-link"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#D4A359] px-5 py-2.5 text-sm font-semibold text-[#1A382B] transition-all duration-300 hover:-translate-y-0.5"
              >
                <Mail size={15} /> Info@kga-schoenwald.de
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
